/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
// React and core libraries
import React, { useContext, useEffect, useState, useRef } from 'react';
import { Link } from 'gatsby';
import {
  ProductProvider,
  ProductPrice,
  AddToCartButton,
} from '@shopify/hydrogen-react';

// Components
import ArrowRight from './icons/ArrowRight';
import AddToWishList from './AddToWishList';

// Hooks
import { useProduct } from '@shopify/hydrogen-react';
import useIntersection from 'buena-suerte/lib/hooks/useIntersection';
import { useShopifyProduct } from '../hooks/useShopifyProduct';

// Utilities and libraries
import classNames from 'classnames';

// Context
import Context from '../context/Context';

const ProductTileInterior = ({
  cents,
  designer,
  shopifyImage,
  title,
  slug,
  notes,
}) => {
  // Refs (none in this component)

  // Global state
  const { setCartOpen } = useContext(Context);

  // Component state
  const [optionsOpen, setOptionsOpen] = useState(false);

  // Product state (from hook)
  let productData = {};
  try {
    productData = useProduct();
  } catch {
    // If useProduct fails, we'll use empty values
    productData = {
      product: null,
      variants: [],
      options: [],
      selectedVariant: null,
      setSelectedVariant: () => {},
    };
  }

  const { product, variants, options, selectedVariant, setSelectedVariant } =
    productData;

  // Effects
  useEffect(() => {
    if (variants && variants.length > 0) {
      const firstAvailableVariant = variants.find(
        variant => variant.availableForSale
      );
      if (firstAvailableVariant) {
        setSelectedVariant(firstAvailableVariant);
      } else if (variants.length > 0) {
        setSelectedVariant(variants[0]);
      }
    }
  }, [variants, setSelectedVariant]);

  return (
    <>
      <Link
        className="product-tile__link"
        to={`/products/${slug}`}
        // state={{ backToCollection, defaultVariant: selectedVariant }}
      >
        <div className="product-tile__image bb--dark-brown x">
          <img src={`${shopifyImage}?w=800&auto=format&q=100`} alt={title} />
        </div>
        <div className="product-tile__details df x fw fdr">
          <div className="product-tile__details--tab br--dark-brown">
            {title && (
              <div className="p2 bb--dark-brown">
                <p className="mono--sm pb1 uppercase">Title</p>
                <p className="sans--sm">{title}</p>
              </div>
            )}

            <div className="p2 ">
              <p className="mono--sm pb1 uppercase">Price</p>
              <div className="df sans--sm aic">
                {product ? (
                  <>
                    <ProductPrice
                      data={product}
                      variantId={selectedVariant?.id}
                      currencyCode="USD"
                      priceType="compareAt"
                      className="pr1 strike color--light-brown w-max"
                      withoutTrailingZeros
                    />
                    <ProductPrice
                      data={product}
                      variantId={selectedVariant?.id}
                      currencyCode="USD"
                      withoutTrailingZeros
                      className="w-max"
                    />
                  </>
                ) : (
                  <span className="w-max">${cents}</span>
                )}
                <span className="pl1">USD</span>
              </div>
            </div>
          </div>
          <div className="product-tile__details--tab">
            {designer && (
              <div className="p2 bb--dark-brown">
                <p className="mono--sm pb1 uppercase">Designer</p>
                <p className="sans--sm">{designer}</p>
              </div>
            )}
            {notes && (
              <div className="p2">
                <p className="mono--sm pb1 uppercase">Details</p>
                <p className="sans--sm">{notes}</p>
              </div>
            )}
          </div>
        </div>
      </Link>
      <div className="product-tile__add-to-cart df x bt--dark-brown sans--sm">
        {product ? (
          <>
            {options?.length === 1 && variants?.length > 1 && (
              <div className="product-tile__options pr">
                <button
                  onClick={() => {
                    setOptionsOpen(!optionsOpen);
                  }}
                  className="product-tile__options-trigger df x aic br--dark-brown"
                >
                  {/* Display selected variant title or default */}
                  {selectedVariant?.title === 'Default Title'
                    ? 'One Size'
                    : `${options[0].name}: ${selectedVariant?.title || ''}`}
                  <ArrowRight
                    className={classNames('product-tile__options-trigger-img', {
                      open: optionsOpen,
                    })}
                  />
                </button>
                <ul
                  className={classNames(
                    'product-tile__options-list pa bb--dark-brown br--dark-brown bl--dark-brown',
                    {
                      open: optionsOpen,
                    }
                  )}
                >
                  {/* Map through variants from useProduct */}
                  {variants?.map(variant => {
                    // Find the corresponding option value if needed for display,
                    // but primarily use the variant object itself
                    const optionValue = options[0].values.find(
                      v => v === variant.title
                    );

                    return (
                      <li key={variant.id}>
                        <button
                          onClick={() => {
                            setOptionsOpen(false);
                            if (variant.availableForSale) {
                              setSelectedVariant(variant);
                            }
                          }}
                          className={classNames(
                            'bt--dark-brown product-tile__option-item p1 x',
                            {
                              disabled: !variant.availableForSale,
                            }
                          )}
                          disabled={!variant.availableForSale}
                        >
                          {variant.title}{' '}
                          {!variant.availableForSale && '(Sold Out)'}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <AddToCartButton
              variantId={selectedVariant?.id}
              className="pt1 pb1 pl2 pr2 sans--md x tl add-to-cart"
              disabled={
                !selectedVariant?.availableForSale || !selectedVariant?.id
              }
              onClick={() => {
                setTimeout(() => {
                  setCartOpen(true);
                }, 200);
              }}
            >
              Add to Cart
            </AddToCartButton>
          </>
        ) : (
          <button
            className="pt1 pb1 pl2 pr2 sans--md x tl add-to-cart"
            disabled
          >
            Out of Stock
          </button>
        )}
      </div>
    </>
  );
};

const ProductTile = ({
  cents,
  designer,
  shopifyImage,
  title,
  slug,
  notes,
  type,
  productId,
  defaultVariant,
}) => {
  const intersectionRef = useRef();

  const product = useShopifyProduct({
    id: `gid://shopify/Product/${productId}`,
  });
  const isIntersected = useIntersection(intersectionRef, {
    threshold: 0.2,
    once: true,
  });

  const content = (
    <ProductTileInterior
      cents={cents}
      designer={designer}
      shopifyImage={shopifyImage}
      title={title}
      slug={slug}
      notes={notes}
    />
  );

  return (
    <div
      ref={intersectionRef}
      aria-label="Product Tile"
      className={classNames('product-tile df fdc reveal relative', {
        'bt--dark-brown bl--dark-brown--xl product-tile--spotlight':
          type === 'spotlight',
        revealed: isIntersected,
      })}
    >
      <AddToWishList productId={productId} variantId={defaultVariant?.id} />
      {product ? (
        <ProductProvider data={product}>{content}</ProductProvider>
      ) : (
        content
      )}
    </div>
  );
};

export default ProductTile;
